import React, { useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Skeleton } from '@mui/material';
import EmptyState from '../EmptyState/EmptyState';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';
import { createTableStyles } from './tableStyles';

export function TableSkeleton({ rowCount = 5, columnCount = 4 }) {
  const { semantic, density } = useFeatureTheme();
  const rowHeight = density?.rowHeight ?? 56;

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          gap: 2,
          alignItems: 'center',
          px: 2.5,
          height: 44,
          bgcolor: semantic.table?.headerBackground ?? semantic.surfaces.surfaceSubtle,
          borderBottom: `1px solid ${semantic.borders.border}`,
        }}
      >
        {Array.from({ length: columnCount }).map((_, colIndex) => (
          <Skeleton key={`table-head-skeleton-${colIndex}`} variant="text" height={16} width="60%" />
        ))}
      </Box>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <Box
          key={`table-skeleton-row-${rowIndex}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
            gap: 2,
            alignItems: 'center',
            px: 2.5,
            height: rowHeight,
            borderBottom: `1px solid ${semantic.borders.border}`,
          }}
        >
          {Array.from({ length: columnCount }).map((__, colIndex) => (
            <Skeleton
              key={`table-skeleton-cell-${rowIndex}-${colIndex}`}
              variant="text"
              height={20}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function DataTable({
  columns = [],
  data = [],
  loading = false,
  enableSorting = false,
  enableRowSelection = false,
  enableTopToolbar = false,
  enableBottomToolbar = false,
  enableColumnActions = false,
  enableColumnFilters = false,
  enableHiding = false,
  /** Keep false for list pages — header scrolls with page; no inner scrollbar. */
  enableStickyHeader = false,
  emptyState,
  skeleton,
  getRowId,
  state,
  initialState,
  'aria-label': ariaLabel = 'Data table',
  muiTableContainerProps,
  muiTableHeadCellProps,
  muiTableHeadRowProps,
  muiTableBodyCellProps,
  muiTableBodyRowProps,
  muiTableProps,
  ...rest
}) {
  const themeTokens = useFeatureTheme();
  const tableStyles = useMemo(() => createTableStyles(themeTokens), [themeTokens]);

  const resolvedEmptyState = emptyState ?? (
    <EmptyState title="No results" description="Try adjusting your search or filters." />
  );

  if (loading && data.length === 0) {
    return skeleton ?? <TableSkeleton rowCount={5} columnCount={columns.length || 4} />;
  }

  const containerProps = {
    ...tableStyles.container,
    ...muiTableContainerProps,
    sx: {
      ...tableStyles.container.sx,
      ...(typeof muiTableContainerProps?.sx === 'object' ? muiTableContainerProps.sx : {}),
    },
  };

  return (
    <Box
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      sx={{
        width: '100%',
        overflow: 'visible',
      }}
    >
      <MaterialReactTable
        columns={columns}
        data={data}
        state={{ isLoading: loading, ...state }}
        initialState={{ density: 'comfortable', ...initialState }}
        getRowId={getRowId}
        enablePagination={false}
        enableSorting={enableSorting}
        enableRowSelection={enableRowSelection}
        enableTopToolbar={enableTopToolbar}
        enableBottomToolbar={enableBottomToolbar}
        enableColumnActions={enableColumnActions}
        enableColumnFilters={enableColumnFilters}
        enableHiding={enableHiding}
        enableStickyHeader={false}
        enableFullScreenToggle={false}
        enableDensityToggle={false}
        enableColumnResizing={false}
        muiTablePaperProps={{
          ...tableStyles.paper,
          sx: {
            ...tableStyles.paper.sx,
            overflow: 'visible',
          },
        }}
        muiTableProps={{
          stickyHeader: false,
          ...tableStyles.table,
          ...muiTableProps,
        }}
        muiTableContainerProps={containerProps}
        muiTableHeadRowProps={{
          ...tableStyles.headRow,
          ...muiTableHeadRowProps,
        }}
        muiTableHeadCellProps={{
          ...tableStyles.headCell,
          ...muiTableHeadCellProps,
          sx: {
            ...tableStyles.headCell.sx,
            position: 'static !important',
            top: 'auto !important',
            ...(typeof muiTableHeadCellProps?.sx === 'object' ? muiTableHeadCellProps.sx : {}),
          },
        }}
        muiTableBodyCellProps={{
          ...tableStyles.bodyCell,
          ...muiTableBodyCellProps,
        }}
        muiTableBodyRowProps={{
          ...tableStyles.bodyRow,
          ...muiTableBodyRowProps,
        }}
        renderEmptyRowsFallback={() => resolvedEmptyState}
        {...rest}
      />
    </Box>
  );
}
